import { Component, OnInit, OnDestroy } from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {faFloppyDisk, faTriangleExclamation} from "@fortawesome/free-solid-svg-icons";
import {map, Observable, Subscription} from "rxjs";
import {MatSnackBar} from "@angular/material/snack-bar";
import { AgreementService } from "../../../data-access/agreement.service";
import {APIResponse} from "../../../../shared/data-access/api-response.model";
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AgreementFormDataService } from './agreement-form-data';
import {BreadcrumbNavigation} from "../../../../shared/ui/breadcrumb/breadcrumb.component";

export interface AgreementDetail {
  agreement_id: string;
  name: string;
  agreement_text: string;
  notice_text: string;
  accept_text: string;
  reject_text: string;
}

@Component({
    selector: 'createEditAgreement',
    templateUrl: './createEditAgreement.html',
    styleUrls: ['createEditAgreement.css'],
    standalone: false
})
export class CreateEditAgreement implements OnInit {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private agreementService: AgreementService,
    private fb: FormBuilder,
    private agreementFormDataService: AgreementFormDataService,
    private _snackBar: MatSnackBar,
) {
  }

  add: boolean | null = null;
  id: string = '';
  openSaveDialog = false
  openExitDialog = false
  faFloppyDisk = faFloppyDisk
  faTriangleExclamation = faTriangleExclamation
  haveChangesBeenMade = false
  openErrorDialog = false
  errorDialogText = "General unknown error"
  primaryColor = `color: ${localStorage.getItem('primaryColor')};`
  checkboxStyle = `accent-color: ${localStorage.getItem('primaryColor')}; height: 15px; margin-right: 0.5rem;`
  form!: FormGroup;
  savedFormData = false;
  endText = ''
  breadcrumbNav: BreadcrumbNavigation[] = [{
    text: 'User Agreement Management',
    route: 'dashboard/AgreementManagement/list'
  }]

  ngOnInit() {
    this.form = this.fb.group({
      title: ['', [Validators.required]],
      body: ['', [Validators.required]],
      notice: ['', [Validators.required]],
      required: [false, [Validators.required]]
    });

    this.route.queryParams.subscribe(params => {
      this.id = params['id']
    });

    this.add = window.location.pathname.includes('add')
    if(this.add)
      this.endText = 'Add User Agreement'
    else
      this.endText = 'Edit User Agreement'

    this.agreementFormDataService.formData$.subscribe(data => {
      if (data) {
        this.form.setValue(data);
        this.savedFormData = true;
      }
    });

    if (!this.add && !this.savedFormData) {
       this.agreementService.agreementDetails(this.id).pipe(
        map((agreement) => {
          return agreement as AgreementDetail
        })
      ).subscribe({
        next: (agreement) => {
          const agreementInfo = {
            title: agreement.name,
            body: agreement.agreement_text,
            notice: agreement.notice_text,
            required: agreement.reject_text === null,
          };
          this.form.patchValue(agreementInfo);
          this.agreementFormDataService.setFormData(agreementInfo);
          this.savedFormData = true;
        },
        error: () => {
          this._snackBar.open('Unable to load the agreement. Please try again.', 'Dismiss', {panelClass: ['error-snackbar']})
        }
      });
      }
  }

  handleSaveResponse(response: APIResponse) {
    if(response.success) {
      this.openSaveDialog = true
      setTimeout(() => {
        this.openSaveDialog = false
        this.router.navigate(['dashboard/AgreementManagement/list']);
      }, 4000)
    } else {
      this.openErrorDialog = true
      this.errorDialogText = response.msg
    }
  }

  save() {
    const {title, body, notice, required} = this.form.value

    if(this.form.valid) {
      if(this.add) {
        this.agreementService.createAgreement(title, body, notice, required).subscribe((response) => {
          this.handleSaveResponse(response)
        })
      }
      else {
        this.agreementService.createAgreement(title, body, notice, required, this.id).subscribe((response) => {
          this.handleSaveResponse(response)
        })
      }
      this.agreementFormDataService.clearFormData()
    } else {
      this.form.markAllAsTouched()
    }
  }

  preview() {
    if (this.form.valid) {
      this.agreementFormDataService.setFormData(this.form.value);
      this.router.navigate(['dashboard/AgreementManagement/preview'], {queryParams: {
        id: this.id,
        add: this.add,
    }});
    }
  }

  closeSaveDialog() {
    this.openSaveDialog = false
  }

  exitDialog() {
    if(this.haveChangesBeenMade)
      this.openExitDialog = true
    else
      this.exitAgreement()
  }

  closeExitDialog() {
    this.openExitDialog = false
  }

  changes() {
    this.haveChangesBeenMade = true
  }

  exitAgreement() {
    this.router.navigate(['dashboard/AgreementManagement/list'])
    this.agreementFormDataService.clearFormData();
  }

  protected readonly localStorage = localStorage;
}
